import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { studyHistoryService } from '../services/studyHistory/StudyHistoryService';
import { wordPressUserService } from '../services/api/WPUserService';

const TestStudyProgress: React.FC = () => {
  const { user } = useAuth();
  const [lessonId, setLessonId] = useState('test-lesson-001');
  const [lessonTitle, setLessonTitle] = useState('Bài học test - Lý thuyết');
  const [progress, setProgress] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [status, setStatus] = useState<'in_progress' | 'completed'>('in_progress');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleStartLesson = async () => {
    if (!user?.id) {
      setError('Vui lòng đăng nhập trước');
      return;
    }

    setLoading(true);
    setError('');
    setResult('');

    try {
      const progressData = await studyHistoryService.startLesson(
        user.id,
        lessonId,
        lessonTitle
      );
      setResult(`✅ Đã bắt đầu bài học: ${progressData.id}`);
      console.log('Started lesson:', progressData);
    } catch (err: any) {
      setError(`❌ Lỗi: ${err.message}`);
      console.error('Error starting lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!user?.id) {
      setError('Vui lòng đăng nhập trước');
      return;
    }

    setLoading(true);
    setError('');
    setResult('');

    try {
      await studyHistoryService.updateLessonProgress(
        user.id,
        lessonId,
        progress,
        timeSpent
      );
      setResult(`✅ Đã cập nhật tiến độ: ${progress}%, thời gian: ${timeSpent}s`);
      console.log('Updated progress:', { progress, timeSpent });
    } catch (err: any) {
      setError(`❌ Lỗi: ${err.message}`);
      console.error('Error updating progress:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteLesson = async () => {
    if (!user?.id) {
      setError('Vui lòng đăng nhập trước');
      return;
    }

    setLoading(true);
    setError('');
    setResult('');

    try {
      // Set progress to 100 and complete
      await studyHistoryService.completeLesson(user.id, lessonId);
      setResult(`🎉 Đã hoàn thành bài học: ${lessonTitle}`);
      setProgress(100);
      setStatus('completed');
      console.log('Completed lesson');
    } catch (err: any) {
      setError(`❌ Lỗi: ${err.message}`);
      console.error('Error completing lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectAPI = async () => {
    if (!user?.id) {
      setError('Vui lòng đăng nhập trước');
      return;
    }

    setLoading(true);
    setError('');
    setResult('');

    try {
      // Gọi trực tiếp API WordPress
      const response = await fetch(`${import.meta.env.VITE_WORDPRESS_API_URL}/wp-json/qoder/v1/study-progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          lessonId: lessonId,
          lessonTitle: lessonTitle,
          progress: progress,
          timeSpent: timeSpent,
          status: status,
          completedAt: status === 'completed' ? new Date().toISOString() : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create study progress');
      }

      const data = await response.json();
      setResult(`✅ Đã gửi trực tiếp API: ${JSON.stringify(data, null, 2)}`);
      console.log('Direct API response:', data);
    } catch (err: any) {
      setError(`❌ Lỗi API: ${err.message}`);
      console.error('Error calling API:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAPI = async () => {
    if (!user?.id) {
      setError('Vui lòng đăng nhập trước');
      return;
    }

    setLoading(true);
    setError('');
    setResult('');

    try {
      // Generate a test ID
      const testId = `test-${Date.now()}`;

      // Gọi API update (sẽ tạo mới nếu không tồn tại)
      const response = await fetch(`${import.meta.env.VITE_WORDPRESS_API_URL}/wp-json/qoder/v1/study-progress/${testId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          lessonId: lessonId,
          lessonTitle: lessonTitle,
          progress: progress,
          timeSpent: timeSpent,
          status: status,
          completedAt: status === 'completed' ? new Date().toISOString() : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update study progress');
      }

      const data = await response.json();
      setResult(`✅ Đã update API: ${JSON.stringify(data, null, 2)}`);
      console.log('Update API response:', data);
    } catch (err: any) {
      setError(`❌ Lỗi API: ${err.message}`);
      console.error('Error calling update API:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">🧪 Test Study Progress - Hoàn thành lý thuyết</h1>

      {!user && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
          ⚠️ Vui lòng đăng nhập để test
        </div>
      )}

      {user && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          👤 Đang đăng nhập với: {user.email} (ID: {user.id})
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Thông tin bài học</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lesson ID
            </label>
            <input
              type="text"
              value={lessonId}
              onChange={(e) => setLessonId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="test-lesson-001"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lesson Title
            </label>
            <input
              type="text"
              value={lessonTitle}
              onChange={(e) => setLessonTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Bài học test"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Progress (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={progress}
                onChange={(e) => setProgress(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time Spent (seconds)
              </label>
              <input
                type="number"
                min="0"
                value={timeSpent}
                onChange={(e) => setTimeSpent(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'in_progress' | 'completed')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Actions</h2>

        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleStartLesson}
            disabled={loading || !user}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : '1. Start Lesson'}
          </button>

          <button
            onClick={handleUpdateProgress}
            disabled={loading || !user}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : '2. Update Progress'}
          </button>

          <button
            onClick={handleCompleteLesson}
            disabled={loading || !user}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : '3. Complete Lesson'}
          </button>

          <button
            onClick={handleDirectAPI}
            disabled={loading || !user}
            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Loading...' : '4. Direct API (POST)'}
          </button>

          <button
            onClick={handleUpdateAPI}
            disabled={loading || !user}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed col-span-2"
          >
            {loading ? 'Loading...' : '5. Update API (PUT) - Tạo mới nếu chưa có'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <pre className="whitespace-pre-wrap text-sm">{result}</pre>
        </div>
      )}

      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mt-6">
        <h3 className="font-semibold mb-2">📝 Hướng dẫn:</h3>
        <ol className="list-decimal list-inside space-y-1 text-sm">
          <li>Nhập thông tin bài học (Lesson ID, Title, Progress, Time Spent)</li>
          <li>Click "Start Lesson" để bắt đầu bài học</li>
          <li>Click "Update Progress" để cập nhật tiến độ</li>
          <li>Click "Complete Lesson" để hoàn thành bài học (progress = 100%)</li>
          <li>Hoặc dùng "Direct API" để gửi trực tiếp qua API</li>
          <li>Dùng "Update API" để test endpoint PUT (tạo mới nếu chưa có)</li>
        </ol>
      </div>
    </div>
  );
};

export default TestStudyProgress;

